import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDeleteNotificationCampaignMutation } from '../../../../services/redux/api/notificationsAdminApi';
import { INotificationCampaign } from '../../../../types/interfaces/notification-admin';
import useFlexibleColumnsTable from '../../../../hooks/useFlexibleColumnsTable';
import { LOCAL_STORAGE_KEYS } from '../../../../shared/settingsNames';
import getNotificationCampaignsTableColumns, {
  getNotificationCampaignsDefaultSettings,
} from './notificationCampaignsTableColumns';
import { getNotificationCampaignsCellReducers } from './getNotificationCampaignsCellReducers';

function languageIdFromFilter(
  filterJson: Record<string, unknown> | null,
): string | null {
  if (!filterJson || typeof filterJson !== 'object') return null;
  const v = filterJson.languageId;
  return typeof v === 'string' ? v : null;
}

export function useNotificationCampaignsListComponent(
  classes: Record<string, string>,
  campaigns: INotificationCampaign[],
  langById: Map<string, string>,
  canEdit: boolean,
  canDelete: boolean,
  onEdit: (row: INotificationCampaign) => void,
) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');
  const [itemForDelete, setItemForDelete] =
    useState<INotificationCampaign | null>(null);

  const includeActions = canEdit || canDelete;

  const filteredValues = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return campaigns;
    return campaigns.filter((c) => {
      const kind = (c.kind ?? 'template').toLowerCase();
      const code = (c.templateCode ?? '').toLowerCase();
      return (
        kind.includes(q) ||
        code.includes(q) ||
        c.status.toLowerCase().includes(q)
      );
    });
  }, [campaigns, filter]);

  const langLabel = (fj: INotificationCampaign['filterJson']) => {
    const lid = languageIdFromFilter(fj);
    if (!lid) return '—';
    return langById.get(lid) ?? lid;
  };

  const { order: defaultOrder, orderBy } =
    getNotificationCampaignsDefaultSettings(t, includeActions);

  const cellReducers = getNotificationCampaignsCellReducers(
    t,
    classes,
    { canEdit, canDelete },
    langLabel,
    onEdit,
    (row) => setItemForDelete(row),
  );

  const useTableData = useFlexibleColumnsTable(
    filteredValues,
    LOCAL_STORAGE_KEYS.NOTIFICATION_CAMPAIGNS_TABLE_SETTINGS,
    getNotificationCampaignsTableColumns(t, includeActions),
    orderBy,
    defaultOrder,
  );

  const {
    order,
    columnsListForPopup,
    columnsList,
    onToggleHideColumn,
    onRestoreDefault,
    sortedArray,
    onSortColumns,
    onSort,
  } = useTableData;

  const [deleteCampaign] = useDeleteNotificationCampaignMutation();

  function onItemDelete() {
    if (!itemForDelete?.id) return;
    deleteCampaign(itemForDelete.id);
    setItemForDelete(null);
  }

  return {
    sortedArray,
    columnsList,
    columnsListForPopup,
    order,
    orderBy,
    cellReducers,
    onSort,
    onSortColumns,
    onToggleHideColumn,
    onRestoreDefault,
    filter,
    setFilter,
    itemForDelete,
    setItemForDelete,
    onItemDelete,
  };
}
